import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';

// ============================================
// Animated Wave Separator
// ============================================

const WaveSeparator: React.FC = () => {
  return (
    <div className="absolute top-0 left-0 right-0 -translate-y-full overflow-hidden">
      <svg
        className="relative w-full h-16 md:h-24"
        viewBox="0 0 1200 120"
        preserveAspectRatio="none"
      >
        <motion.path
          d="M0,60 C150,100 350,0 600,60 C850,120 1050,20 1200,60 L1200,120 L0,120 Z"
          fill="#0a0a0a"
          initial={{ d: "M0,60 C150,100 350,0 600,60 C850,120 1050,20 1200,60 L1200,120 L0,120 Z" }}
          animate={{
            d: [
              "M0,60 C150,100 350,0 600,60 C850,120 1050,20 1200,60 L1200,120 L0,120 Z",
              "M0,80 C150,40 350,100 600,40 C850,0 1050,80 1200,40 L1200,120 L0,120 Z",
              "M0,60 C150,100 350,0 600,60 C850,120 1050,20 1200,60 L1200,120 L0,120 Z",
            ],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.path
          d="M0,80 C200,120 400,40 600,80 C800,120 1000,40 1200,80 L1200,120 L0,120 Z"
          fill="#0a0a0a"
          opacity={0.5}
          initial={{ d: "M0,80 C200,120 400,40 600,80 C800,120 1000,40 1200,80 L1200,120 L0,120 Z" }}
          animate={{
            d: [
              "M0,80 C200,120 400,40 600,80 C800,120 1000,40 1200,80 L1200,120 L0,120 Z",
              "M0,60 C200,20 400,100 600,60 C800,20 1000,100 1200,60 L1200,120 L0,120 Z",
              "M0,80 C200,120 400,40 600,80 C800,120 1000,40 1200,80 L1200,120 L0,120 Z",
            ],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
        />
      </svg>
    </div>
  );
};

// ============================================
// Newsletter Input with Floating Label
// ============================================

const NewsletterInput: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
      }, 3000);
    }, 1500);
  }, [email]);

  const isActive = isFocused || email.length > 0;

  return (
    <form onSubmit={handleSubmit} className="relative">
      <div className="relative">
        {/* Floating label */}
        <motion.label
          className="absolute left-4 pointer-events-none text-zinc-500 origin-left"
          animate={{
            y: isActive ? -24 : 0,
            scale: isActive ? 0.85 : 1,
            color: isFocused ? '#10b981' : '#71717a',
          }}
          transition={{ duration: 0.2 }}
        >
          Enter your email
        </motion.label>

        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={isSubmitted}
          className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all disabled:opacity-50"
        />

        {/* Submit button */}
        <motion.button
          type="submit"
          disabled={!email || isLoading || isSubmitted}
          className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-700 text-white text-sm font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"
              />
            ) : isSubmitted ? (
              <motion.svg
                key="success"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <motion.path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.svg>
            ) : (
              <motion.span
                key="text"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                Subscribe
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      {/* Success message */}
      <AnimatePresence>
        {isSubmitted && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full mt-2 text-emerald-400 text-sm"
          >
            Thanks for subscribing!
          </motion.p>
        )}
      </AnimatePresence>
    </form>
  );
};

// ============================================
// Animated Link with Underline Draw
// ============================================

interface AnimatedLinkProps {
  href: string;
  children: React.ReactNode;
}

const AnimatedLink: React.FC<AnimatedLinkProps> = ({ href, children }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.a
      href={href}
      className="relative text-zinc-500 hover:text-white text-sm transition-colors inline-block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      <motion.span
        className="absolute bottom-0 left-0 h-px bg-gradient-to-r from-emerald-400 to-blue-400"
        initial={{ width: 0 }}
        animate={{ width: isHovered ? '100%' : 0 }}
        transition={{ duration: 0.3, ease: [0.215, 0.61, 0.355, 1] }}
      />
    </motion.a>
  );
};

// ============================================
// Logo with Glow Pulse
// ============================================

const AnimatedLogo: React.FC = () => {
  return (
    <motion.a
      href="/"
      className="relative inline-block"
      whileHover={{ scale: 1.02 }}
    >
      {/* Glow effect */}
      <motion.div
        className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-violet-500/20 rounded-2xl blur-xl"
        animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <span className="relative text-2xl font-bold text-white tracking-tight">
        AI
        <motion.span
          className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400"
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{ backgroundSize: '200% 200%' }}
        >
          Deploy
        </motion.span>
      </span>
    </motion.a>
  );
};

// ============================================
// Back to Top Button
// ============================================

const BackToTopButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          onClick={scrollToTop}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 w-10 h-10 sm:w-12 sm:h-12 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-full flex items-center justify-center text-white shadow-lg shadow-black/20 z-40"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <motion.svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            animate={{ rotate: isHovered ? -360 : 0 }}
            transition={{ duration: 0.5 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </motion.svg>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

// ============================================
// Live Chat Widget Preview
// ============================================

const LiveChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(true);

  return (
    <div className="fixed bottom-4 right-16 sm:bottom-8 sm:right-24 z-40">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="absolute bottom-16 right-0 w-[calc(100vw-2rem)] sm:w-80 max-w-80 bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-emerald-500 to-teal-500">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-semibold">AI Support</p>
                  <p className="text-white/80 text-sm flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    Online now
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="p-4 space-y-3 h-48 overflow-y-auto">
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex-shrink-0 flex items-center justify-center">
                  <span className="text-white text-xs">AI</span>
                </div>
                <div className="bg-zinc-800 rounded-2xl rounded-tl-sm p-3 text-sm text-gray-300">
                  Hi! How can I help you today?
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex-shrink-0 flex items-center justify-center">
                  <span className="text-white text-xs">AI</span>
                </div>
                <div className="bg-zinc-800 rounded-2xl rounded-tl-sm p-3 text-sm text-gray-300">
                  I can answer questions about our AI services or help you book a demo.
                </div>
              </div>
            </div>

            {/* Input */}
            <div className="p-4 border-t border-zinc-800">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type a message..."
                  className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
                />
                <button className="px-3 py-2 bg-emerald-500 hover:bg-emerald-400 rounded-lg transition-colors">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle button */}
      <motion.button
        onClick={() => {
          setIsOpen(!isOpen);
          setHasNewMessage(false);
        }}
        className="relative w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/25"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.svg
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              className="w-6 h-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </motion.svg>
          ) : (
            <motion.svg
              key="chat"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="w-6 h-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </motion.svg>
          )}
        </AnimatePresence>

        {/* New message indicator */}
        {hasNewMessage && !isOpen && (
          <motion.span
            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-[#0a0a0a]"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
        )}
      </motion.button>
    </div>
  );
};

// ============================================
// Trust Badge with Tooltip
// ============================================

interface TrustBadgeProps {
  icon: React.ReactNode;
  label: string;
  tooltip: string;
}

const TrustBadge: React.FC<TrustBadgeProps> = ({ icon, label, tooltip }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <motion.div
        className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-zinc-800/50 border border-zinc-700/50 rounded-lg cursor-default"
        whileHover={{ scale: 1.02, borderColor: 'rgba(255,255,255,0.2)' }}
      >
        <span className="text-emerald-400 [&>svg]:w-4 [&>svg]:h-4 sm:[&>svg]:w-5 sm:[&>svg]:h-5">{icon}</span>
        <span className="text-zinc-400 text-xs sm:text-sm">{label}</span>
      </motion.div>

      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl whitespace-nowrap z-50"
          >
            <span className="text-white text-sm">{tooltip}</span>
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-800" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// Footer Column with Scroll Reveal
// ============================================

interface FooterColumnProps {
  title: string;
  children: React.ReactNode;
  delay?: number;
}

const FooterColumn: React.FC<FooterColumnProps> = ({ title, children, delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.215, 0.61, 0.355, 1] }}
    >
      <h3 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">
        {title}
      </h3>
      {children}
    </motion.div>
  );
};

// ============================================
// Footer Links Data
// ============================================

const footerLinks = {
  services: [
    { label: 'AI Audit Partnership', href: '/services/ai-audit-partnership' },
    { label: 'AI Implementation', href: '/services/ai-implementation' },
    { label: 'VibeCode Interfaces', href: '/services/vibecode' },
  ],
  company: [
    { label: 'About', href: '/about' },
    { label: 'Case Studies', href: '/case-studies' },
    // { label: 'Blog', href: '/blog' }, // Hidden for now
    // { label: 'Careers', href: '/careers' }, // Hidden for now
  ],
  legal: [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
  ],
};

// ============================================
// Main Footer Component
// ============================================

export const Footer: React.FC = () => {
  const footerRef = useRef<HTMLElement>(null);
  const isInView = useInView(footerRef, { once: true, margin: '-100px' });

  return (
    <>
      <footer ref={footerRef} className="relative bg-[#0a0a0a] pt-8">
        {/* Animated wave separator */}
        <WaveSeparator />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 sm:gap-10 lg:gap-12 mb-12 sm:mb-16">
            {/* Brand Column */}
            <motion.div
              className="col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-2"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <AnimatedLogo />

              <p className="text-zinc-500 text-sm leading-relaxed my-6 max-w-sm">
                We build and deploy AI systems that cut operating costs 30-50% — in weeks, not months.
              </p>

              {/* Newsletter */}
              <div className="mb-8">
                <p className="text-white text-sm font-medium mb-4">Stay updated with AI insights</p>
                <NewsletterInput />
              </div>

            </motion.div>

            {/* Services */}
            <FooterColumn title="Services" delay={0.1}>
              <ul className="space-y-3">
                {footerLinks.services.map((link) => (
                  <li key={link.href}>
                    <AnimatedLink href={link.href}>{link.label}</AnimatedLink>
                  </li>
                ))}
              </ul>
            </FooterColumn>

            {/* Company */}
            <FooterColumn title="Company" delay={0.2}>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.href}>
                    <AnimatedLink href={link.href}>{link.label}</AnimatedLink>
                  </li>
                ))}
              </ul>
            </FooterColumn>

            {/* Contact */}
            <FooterColumn title="Contact" delay={0.3}>
              <ul className="space-y-3">
                <li>
                  <AnimatedLink href="mailto:hello@aideploy.com">hello@aideploy.com</AnimatedLink>
                </li>
                <li>
                  <AnimatedLink href="/contact">Book a Call</AnimatedLink>
                </li>
              </ul>
            </FooterColumn>
          </div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-2 sm:gap-4 justify-center mb-8 sm:mb-12"
          >
            <TrustBadge
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              }
              label="SOC 2 Compliant"
              tooltip="Enterprise-grade security and compliance"
            />
            <TrustBadge
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              }
              label="256-bit Encryption"
              tooltip="Bank-level data encryption"
            />
            <TrustBadge
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              }
              label="GDPR Ready"
              tooltip="Full compliance with EU data regulations"
            />
            <TrustBadge
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              label="99.9% Uptime"
              tooltip="Industry-leading reliability SLA"
            />
          </motion.div>

          {/* Bottom */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="pt-8 border-t border-zinc-800/50 flex flex-col md:flex-row justify-between items-center gap-4"
          >
            <p className="text-sm text-zinc-600">
              &copy; {new Date().getFullYear()} AIDeploy. All rights reserved.
            </p>
            <div className="flex gap-6">
              {footerLinks.legal.map((link) => (
                <AnimatedLink key={link.href} href={link.href}>
                  {link.label}
                </AnimatedLink>
              ))}
            </div>
          </motion.div>
        </div>
      </footer>

      {/* Floating elements */}
      <BackToTopButton />
      <LiveChatWidget />
    </>
  );
};

export default Footer;
